import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { Sparkles, Info } from 'lucide-react';

// Body regions with associated sensations
const BODY_REGIONS = {
  head: {
    label: 'Head',
    position: { top: '5%', left: '50%' },
    sensations: ['tension', 'pressure', 'throbbing', 'foggy', 'clear', 'dizzy', 'tight scalp'],
    emotionLinks: ['stress', 'anxiety', 'overwhelm', 'concentration']
  },
  face: {
    label: 'Face',
    position: { top: '12%', left: '50%' },
    sensations: ['flushed', 'hot', 'tears', 'tight jaw', 'clenched teeth', 'relaxed', 'numb'],
    emotionLinks: ['anger', 'embarrassment', 'sadness', 'joy']
  },
  throat: {
    label: 'Throat',
    position: { top: '22%', left: '50%' },
    sensations: ['tight', 'lump', 'constricted', 'open', 'choked', 'dry'],
    emotionLinks: ['fear', 'grief', 'unsaid words', 'anxiety']
  },
  shoulders: {
    label: 'Shoulders',
    position: { top: '28%', left: '50%' },
    sensations: ['tense', 'raised', 'heavy', 'light', 'aching', 'relaxed', 'hunched'],
    emotionLinks: ['burden', 'responsibility', 'stress', 'relief']
  },
  chest: {
    label: 'Chest',
    position: { top: '38%', left: '50%' },
    sensations: ['tight', 'racing heart', 'warm', 'expansive', 'heavy', 'fluttery', 'aching', 'open'],
    emotionLinks: ['anxiety', 'love', 'grief', 'joy', 'fear']
  },
  stomach: {
    label: 'Stomach',
    position: { top: '50%', left: '50%' },
    sensations: ['butterflies', 'knots', 'nausea', 'empty', 'full', 'churning', 'settled', 'tight'],
    emotionLinks: ['anxiety', 'excitement', 'dread', 'intuition', 'fear']
  },
  hands: {
    label: 'Hands',
    position: { top: '55%', left: '25%' },
    sensations: ['clenched', 'sweaty', 'tingling', 'cold', 'warm', 'shaky', 'relaxed', 'numb'],
    emotionLinks: ['anxiety', 'anger', 'nervous energy', 'fear']
  },
  legs: {
    label: 'Legs',
    position: { top: '75%', left: '50%' },
    sensations: ['heavy', 'restless', 'weak', 'strong', 'shaky', 'grounded', 'jittery', 'frozen'],
    emotionLinks: ['fear (flight response)', 'stability', 'groundedness', 'anxiety']
  },
  wholeBody: {
    label: 'Whole Body',
    position: { top: '90%', left: '50%' },
    sensations: ['tense', 'relaxed', 'energised', 'drained', 'numb', 'alive', 'heavy', 'light', 'agitated'],
    emotionLinks: ['general wellbeing', 'stress level', 'energy state']
  }
};

// Common sensation descriptions for psychoeducation
const SENSATION_INFO: Record<string, { description: string; commonWith: string[] }> = {
  'butterflies': {
    description: 'A fluttering feeling in the stomach, often associated with the vagus nerve response',
    commonWith: ['excitement', 'anxiety', 'anticipation']
  },
  'tight': {
    description: 'Muscle tension that often occurs when the body is preparing for action or protection',
    commonWith: ['stress', 'fear', 'anger', 'holding back']
  },
  'heavy': {
    description: 'A weighty sensation that can indicate emotional burden or fatigue',
    commonWith: ['sadness', 'grief', 'exhaustion', 'depression']
  },
  'racing heart': {
    description: 'Increased heart rate from sympathetic nervous system activation',
    commonWith: ['anxiety', 'fear', 'excitement', 'anger']
  },
  'numb': {
    description: 'A disconnection from sensation, often a protective response to overwhelm',
    commonWith: ['dissociation', 'shock', 'emotional overload']
  }
};

interface BodySensation {
  region: string;
  sensations: string[];
  intensity: number;
  notes?: string;
}

interface BodySensationMapperProps {
  onSensationsChange: (sensations: BodySensation[]) => void;
  initialSensations?: BodySensation[];
  compact?: boolean;
}

export function BodySensationMapper({ 
  onSensationsChange, 
  initialSensations = [],
  compact = false 
}: BodySensationMapperProps) {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [bodySensations, setBodySensations] = useState<BodySensation[]>(initialSensations);
  const [notes, setNotes] = useState('');

  const handleRegionClick = (region: string) => {
    setSelectedRegion(region === selectedRegion ? null : region);
  };

  const handleSensationToggle = (region: string, sensation: string) => {
    const existing = bodySensations.find(s => s.region === region);
    let updated: BodySensation[];

    if (existing) {
      if (existing.sensations.includes(sensation)) {
        // Remove sensation
        const newSensations = existing.sensations.filter(s => s !== sensation);
        if (newSensations.length === 0) {
          updated = bodySensations.filter(s => s.region !== region);
        } else {
          updated = bodySensations.map(s => 
            s.region === region ? { ...s, sensations: newSensations } : s
          );
        }
      } else {
        // Add sensation
        updated = bodySensations.map(s => 
          s.region === region ? { ...s, sensations: [...s.sensations, sensation] } : s
        );
      }
    } else {
      // New region
      updated = [...bodySensations, { region, sensations: [sensation], intensity: 5 }];
    }

    setBodySensations(updated);
    onSensationsChange(updated);
  };

  const handleIntensityChange = (region: string, intensity: number) => {
    const updated = bodySensations.map(s => 
      s.region === region ? { ...s, intensity } : s
    );
    setBodySensations(updated);
    onSensationsChange(updated);
  };

  const getRegionSensations = (region: string): string[] => {
    return bodySensations.find(s => s.region === region)?.sensations || [];
  };

  const getRegionIntensity = (region: string): number => {
    return bodySensations.find(s => s.region === region)?.intensity || 5;
  };

  const hasRegionSensations = (region: string): boolean => {
    return bodySensations.some(s => s.region === region && s.sensations.length > 0);
  };

  const selectedRegionData = selectedRegion ? BODY_REGIONS[selectedRegion as keyof typeof BODY_REGIONS] : null;

  return (
    <Card className={compact ? '' : 'border-primary/20'}>
      {!compact && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Body Sensation Mapping
          </CardTitle>
          <CardDescription>
            Tap on body areas to record physical sensations. Our emotions live in our bodies - 
            noticing where you feel things builds emotional awareness.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className={compact ? 'pt-4' : ''}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Body Outline */}
          <div className="relative h-80 bg-muted/30 rounded-lg border overflow-hidden">
            {/* Simple body outline using regions */}
            <svg 
              viewBox="0 0 100 150" 
              className="absolute inset-0 w-full h-full opacity-20"
              style={{ padding: '10%' }}
            >
              {/* Head */}
              <circle cx="50" cy="15" r="10" fill="currentColor" />
              {/* Body */}
              <ellipse cx="50" cy="45" rx="15" ry="20" fill="currentColor" />
              {/* Arms */}
              <line x1="35" y1="35" x2="15" y2="65" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
              <line x1="65" y1="35" x2="85" y2="65" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
              {/* Legs */}
              <line x1="42" y1="65" x2="35" y2="110" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
              <line x1="58" y1="65" x2="65" y2="110" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
            </svg>

            {/* Interactive Regions */}
            {Object.entries(BODY_REGIONS).map(([key, region]) => {
              const isSelected = selectedRegion === key;
              const hasSensations = hasRegionSensations(key);
              
              return (
                <motion.button
                  key={key}
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full transition-all ${
                    isSelected 
                      ? 'bg-primary text-primary-foreground z-20 ring-2 ring-primary ring-offset-2' 
                      : hasSensations 
                        ? 'bg-accent text-accent-foreground z-10' 
                        : 'bg-muted hover:bg-muted-foreground/20 text-muted-foreground'
                  }`}
                  style={{
                    top: region.position.top,
                    left: region.position.left,
                    padding: isSelected ? '8px 12px' : '6px 10px',
                    fontSize: isSelected ? '0.75rem' : '0.65rem'
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleRegionClick(key)}
                >
                  {region.label}
                  {hasSensations && !isSelected && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full" />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Sensation Selection */}
          <div className="space-y-4">
            {selectedRegionData ? (
              <>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <h4 className="font-semibold mb-1">{selectedRegionData.label}</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Often connected to: {selectedRegionData.emotionLinks.join(', ')}
                  </p>
                  
                  {/* Sensation Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedRegionData.sensations.map(sensation => {
                      const isSelected = getRegionSensations(selectedRegion!).includes(sensation);
                      return (
                        <Badge
                          key={sensation}
                          variant={isSelected ? 'default' : 'outline'}
                          className="cursor-pointer transition-all hover:scale-105"
                          onClick={() => handleSensationToggle(selectedRegion!, sensation)}
                        >
                          {sensation}
                        </Badge>
                      );
                    })}
                  </div>

                  {/* Intensity Slider */}
                  {getRegionSensations(selectedRegion!).length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Intensity: {getRegionIntensity(selectedRegion!)}/10
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={getRegionIntensity(selectedRegion!)}
                        onChange={(e) => handleIntensityChange(selectedRegion!, parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                {/* Sensation Info */}
                {getRegionSensations(selectedRegion!).some(s => SENSATION_INFO[s]) && (
                  <div className="p-3 rounded-lg bg-muted/50 border">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="text-xs text-muted-foreground">
                        {getRegionSensations(selectedRegion!)
                          .filter(s => SENSATION_INFO[s])
                          .map(s => (
                            <p key={s} className="mb-1">
                              <strong className="text-foreground">{s}:</strong> {SENSATION_INFO[s].description}
                            </p>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-center p-4">
                <div>
                  <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">
                    Tap on a body region to record sensations
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    This helps connect emotions to physical experience
                  </p>
                </div>
              </div>
            )}

            {/* Summary of Selected Sensations */}
            {bodySensations.length > 0 && (
              <Card className="bg-accent/5 border-accent/20">
                <CardContent className="pt-4">
                  <h4 className="text-sm font-semibold mb-2">Recorded Sensations</h4>
                  <div className="space-y-2">
                    {bodySensations.map(({ region, sensations, intensity }) => (
                      <div key={region} className="flex items-center justify-between text-sm">
                        <span className="font-medium capitalize">{region}:</span>
                        <span className="text-muted-foreground">
                          {sensations.join(', ')} ({intensity}/10)
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Notes */}
        {!compact && (
          <div className="mt-4 space-y-2">
            <label className="text-sm font-medium">Additional notes about body sensations</label>
            <Textarea
              placeholder="Describe any other physical sensations you're noticing..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[60px]"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { BODY_REGIONS };
